import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import spaceManagementService from "../../../services/host-apiServices/spaceManagementService";
import ReusableMapView from "../../../components/ReusableMapView";
import { showApiMessage } from "../../../utils/messageHandler";
import toastUtils from "../../../utils/toast";
import { getEventName } from "../../../utils/spaceNamingUtils";
import { ChevronRight } from "lucide-react";
import {
  getNormalizedDurationType,
  formatDurationType,
  formatDurationUnit,
} from "../../../utils/bookingTimeframeUtils";
import ReusableBookingModal from "../../../components/ReusableBookingModal";
import HImageOverlay from "../../../hcomponents/HImageOverlay";
import logger from "../../../utils/logger";


const SpaceDetail = () => {
  const { spaceId } = useParams();
  const history = useHistory();
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [overlayIndex, setOverlayIndex] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    spaceManagementService
      .getSpaceById(Number(spaceId))
      .then(res => {
        if (res?.status === 200 && res?.data) {
          setSpace(res.data);
          setLoading(false);
        } else {
          throw new Error("Invalid response from server");
        }
      })
      .catch(err => {
        logger.error("Error fetching space:", err);
        // Use showApiMessage for API errors
        if (err?.response || err?.status) {
          showApiMessage(err, "load space details");
        } else {
          toastUtils.error("Failed to load space details");
        }
        setError("Failed to load space details");
        setLoading(false);
      });
  }, [spaceId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center w-full min-h-screen">
        <div className="text-lg text-gray-500">Loading space details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center w-full min-h-screen">
        <div className="text-lg text-red-500">{error}</div>
      </div>
    );
  }

  // Extract data from the real API response
  const images = space?.media?.images || [];
  const videos = space?.media?.videos || [];
  const mainImage = images[0] || "";
  const galleryImages = images.slice(1, 6);
  const firstInstance = space?.instances?.[0];
  const buildingAddress = firstInstance?.location?.buildingAddress;

  const title = firstInstance?.title || getEventName(space, firstInstance) || "Space";
  const location = buildingAddress
    ? [buildingAddress.streetAddress, buildingAddress.city, buildingAddress.region, buildingAddress.country]
        .filter(Boolean)
        .join(", ")
    : "Location not available";
  const description = space?.description || "No description available.";
  const amenities = space?.amenities || [];
  const capacity = space?.capacity || 0;
  const price = space?.ratePrice || 0;
  const currency = space?.currency || "KES";
  const durationType = getNormalizedDurationType(space); // Get duration from space object (HOURLY, DAILY, WEEKLY, MONTHLY)
  const billingPeriodLabel = formatDurationType(durationType);
  const durationUnit = formatDurationUnit(durationType);
  const subCategoryName = space?.subCategory?.name || "Space";
  const categoryName = space?.category?.name || "Work";
  const spaceTypeName = space?.spaceType || "Standard";

  // Additional details from the API
  const size = space?.size || "Not specified";
  const minDuration = space?.minimumDuration || 1;
  const maxDuration = space?.maximumDuration || 24;
  const _bufferTime = space?.bufferTimeInMinutes || 0;
  const availableInstancesCount = space?.availableInstancesCount || space?.instances?.length || 0;
  const addons = space?.addons || [];
  const status = space?.status || "UNPUBLISHED";

  const isPlayServiceWithOfflineTickets = (() => {
    const isPlay = space?.category?.name === "Play" || space?.isPlayService;
    if (!isPlay) return false;
    const tickets = space?.tickets || space?.spaceTickets || [];
    if (tickets.length === 0) return false;
    return tickets.every(t => t.ticketType === "OFFLINE");
  })();

  const ticketUrl = (() => {
    const tickets = space?.tickets || space?.spaceTickets || [];
    return tickets.find(t => t.ticketType === "OFFLINE")?.url;
  })();

  // Create space data for map display
  const mapSpaceData = firstInstance
    ? [
        {
          id: space.id,
          name: firstInstance.title || getEventName(space, firstInstance) || space.name || "Space",
          status: space.status === "PUBLISHED" ? "available" : "unavailable",
          location: location,
          pricing: `${currency} ${price.toLocaleString()}`,
          capacity: capacity,
          image: mainImage,
          coordinates: buildingAddress
            ? {
                latitude: buildingAddress.latitude?.toString(),
                longitude: buildingAddress.longitude?.toString(),
              }
            : null,
        },
      ]
    : [];

  return (
    <div className="flex flex-col items-center w-full min-h-screen">
      <div className="px-4 py-8 mx-auto w-full max-w-7xl">
        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
          <button onClick={() => history.push("/host/book-space")} className="hover:text-gray-900">
            Book Space
          </button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900 font-medium">{title}</span>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Main Content */}
          <div className="flex flex-col gap-6 lg:col-span-2">
            {/* Image Gallery */}
            <div className="overflow-hidden p-4 w-full bg-white rounded-xl shadow">
              <div className="aspect-[16/7] bg-gray-200 flex items-center justify-center rounded-lg mb-2">
                {mainImage ? (
                  <img
                    src={mainImage}
                    alt={title}
                    className="object-cover w-full h-full rounded-lg cursor-pointer hover:opacity-95 transition-opacity"
                    onClick={() => {
                      setOverlayIndex(0);
                      setIsOverlayOpen(true);
                    }}
                  />
                ) : (
                  <span className="text-gray-400">[No Image Available]</span>
                )}
              </div>
              {galleryImages.length > 0 && (
                <div className="flex gap-2 overflow-x-auto">
                  {galleryImages.map((imgUrl, idx) => (
                    <img
                      key={idx}
                      src={imgUrl}
                      alt=""
                      className="object-cover w-16 h-12 rounded flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => {
                        setOverlayIndex(idx + 1); // 0 is main image
                        setIsOverlayOpen(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Video Gallery */}
            {videos.length > 0 && (
              <div className="overflow-hidden p-4 w-full bg-white rounded-xl shadow">
                <h3 className="mb-3 text-lg font-semibold">Videos</h3>
                <div className="space-y-3">
                  {videos.map((videoUrl, idx) => (
                    <div key={idx} className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                      <video controls className="w-full h-full" preload="metadata">
                        <source src={videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Title, Location */}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
              <div className="mb-2 text-gray-500">{location}</div>
            </div>
            {/* About */}
            <div className="p-6 bg-white rounded-xl shadow">
              <h2 className="mb-2 text-lg font-semibold">About this space</h2>
              <p className="mb-4 text-gray-700">{description}</p>

              {/* Space Details Grid */}
              <div className="grid grid-cols-2 gap-4 p-4 mb-4 bg-gray-50 rounded-lg md:grid-cols-4">
                <div>
                  <div className="text-xs text-gray-500">Size</div>
                  <div className="font-medium">{size}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Capacity</div>
                  <div className="font-medium">{capacity} people</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Space Type</div>
                  <div className="font-medium">{spaceTypeName}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Status</div>
                  <div className="font-medium">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        status === "PUBLISHED" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Amenities */}
              <div className="mb-4">
                <h3 className="mb-2 font-semibold">Amenities & Features</h3>
                <div className="flex flex-wrap gap-2">
                  {amenities.length > 0 ? (
                    amenities.map((a, index) => (
                      <span
                        key={typeof a === "object" ? a.id : index}
                        className="px-3 py-1 text-xs bg-blue-50 text-blue-700 rounded-full"
                      >
                        {typeof a === "object" ? a.name : a}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400">No amenities listed.</span>
                  )}
                </div>
              </div>

              {/* Addons */}
              {addons.length > 0 && (
                <div className="mb-4">
                  <h3 className="mb-2 font-semibold">Available Add-ons</h3>
                  <div className="space-y-2">
                    {addons.slice(0, 5).map((addon, index) => (
                      <div key={addon.id || index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                        <div>
                          <div className="font-medium">{addon.name}</div>
                          {addon.description && <div className="text-sm text-gray-600">{addon.description}</div>}
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-blue-700">
                            {addon.unitPrice
                              ? `${currency} ${(addon.unitPrice || 0).toLocaleString()}`
                              : "Price on request"}
                          </div>
                          <div className="text-xs text-gray-500">{addon.duration || "Per session"}</div>
                        </div>
                      </div>
                    ))}
                    {addons.length > 5 && (
                      <div className="text-sm text-gray-500 text-center">
                        +{addons.length - 5} more add-ons available
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Location & Directions */}
            <div className="p-6 bg-white rounded-xl shadow">
              <h3 className="mb-2 font-semibold">Location & Directions</h3>
              <div className="mb-2">
                <div className="w-full h-48 rounded overflow-hidden">
                  {buildingAddress?.latitude && buildingAddress?.longitude ? (
                    <ReusableMapView
                      spaces={mapSpaceData}
                      showLegend={false}
                      showQuickView={false}
                      className="h-full"
                    />
                  ) : (
                    <div className="flex justify-center items-center w-full h-full bg-gray-200 rounded">
                      <span className="text-gray-400">Map not available - No coordinates</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-2">
                <div className="text-sm font-medium">Address</div>
                <div className="flex gap-2 items-center">
                  <span className="text-gray-700">{location}</span>
                  <button
                    onClick={() => {
                      let googleMapsUrl;
                      // Use lat/long if available for exact location
                      if (buildingAddress?.latitude && buildingAddress?.longitude) {
                        googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${buildingAddress.latitude},${buildingAddress.longitude}`;
                      } else {
                        // Fallback to address string
                        const address = buildingAddress
                          ? `${buildingAddress.streetAddress}, ${buildingAddress.city}, ${buildingAddress.country}`
                          : location;
                        googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
                      }
                      window.open(googleMapsUrl, "_blank");
                    }}
                    className="px-2 py-1 ml-2 text-xs bg-gray-100 rounded border border-gray-300 hover:bg-gray-200"
                  >
                    Get Directions
                  </button>
                </div>
                <div className="text-xs text-gray-500">
                  {buildingAddress?.city}, {buildingAddress?.country}
                </div>
              </div>
              <div className="mb-2">
                <div className="text-sm font-medium">Building Information</div>
                <div className="text-sm text-gray-700">
                  <span>Building: {buildingAddress?.buildingName || "-"}</span>
                  <span className="ml-4">Floor: {firstInstance?.location?.floorLevel || "-"}</span>
                </div>
              </div>
            </div>
            {/* Booking Rules & Access */}
            <div className="p-6 bg-white rounded-xl shadow">
              <h3 className="mb-2 font-semibold">Booking Rules & Duration</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div>
                  <div className="mb-1 text-xs text-gray-500">Minimum Duration</div>
                  <div className="text-sm font-medium">
                    {minDuration} {durationUnit}
                    {minDuration !== 1 ? "s" : ""}
                  </div>
                </div>
                <div>
                  <div className="mb-1 text-xs text-gray-500">Maximum Duration</div>
                  <div className="text-sm font-medium">
                    {maxDuration} {durationUnit}
                    {maxDuration !== 1 ? "s" : ""}
                  </div>
                </div>
                <div>
                  <div className="mb-1 text-xs text-gray-500">Available Instances</div>
                  <div className="text-sm font-medium">{availableInstancesCount} locations</div>
                </div>
                <div>
                  <div className="mb-1 text-xs text-gray-500">Cancellation Policy</div>
                  <div className="text-sm font-medium">{space?.cancellationPolicy?.name || "24 hours notice"}</div>
                </div>
              </div>
            </div>
          </div>
          {/* Sidebar */}
          <div className="flex flex-col gap-6 lg:col-span-1">
            {/* Booking Card */}
            <div className="sticky top-8 p-6 bg-white rounded-xl shadow">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-700">👥 Up to {capacity} people</span>
                <button className="px-2 py-1 text-xs text-gray-500 rounded border border-gray-300">Share</button>
              </div>
              <div className="mb-4">
                <div className="mb-1 text-xs text-gray-500">Pricing</div>
                <div className="flex gap-4 items-center">
                  <div className="flex flex-col">
                    <span className="text-sm">Rate</span>
                    <span className="text-2xl font-bold text-blue-700">
                      {currency} {price.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-500">{billingPeriodLabel}</span>
                  </div>
                </div>
              </div>

              {/* Space Category */}
              <div className="mb-4 p-3 bg-gray-50 rounded">
                <div className="mb-1 text-xs text-gray-500">Category</div>
                <div className="font-medium">{categoryName}</div>
                <div className="text-sm text-gray-600">{subCategoryName}</div>
              </div>
              <button
                onClick={() => {
                  if (isPlayServiceWithOfflineTickets && ticketUrl) {
                    let finalUrl = ticketUrl;
                    if (!finalUrl.startsWith("http://") && !finalUrl.startsWith("https://")) {
                      finalUrl = "https://" + finalUrl;
                    }
                    window.open(finalUrl, "_blank");
                  } else {
                    setShowBookingModal(true);
                  }
                }}
                className="py-3 w-full font-semibold text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition-colors"
              >
                {isPlayServiceWithOfflineTickets ? "Buy Ticket" : "Book This Space"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && space && (
        <ReusableBookingModal
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
          space={space}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          existingBookings={[]}
          availabilityInstanceIds={[]}
          timeFormat="12h"
          closeOnSuccess={true}
          onBookingSuccess={() => {
            toastUtils.success("Booking confirmed successfully!");
            setShowBookingModal(false);
          }}
          onBookingError={error => {
            logger.error("Booking error:", error);
            showApiMessage(error, "create booking");
          }}
        />
      )}

      {/* Image Overlay */}
      {isOverlayOpen && (
        <HImageOverlay
          isOpen={isOverlayOpen}
          onClose={() => setIsOverlayOpen(false)}
          images={images}
          currentIndex={overlayIndex}
          onIndexChange={setOverlayIndex}
        />
      )}
    </div>
  );
};

export default SpaceDetail;
